import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faPhone, faEnvelope, faLock, faMapMarkerAlt, faCheckCircle, faUserFriends, faCity, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { sendApiRequest } from "../utils";
import Swal from 'sweetalert2';
import Loader from "./Loader";
import { useNavigate, useSearchParams } from "react-router-dom";

const SignUpForm = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sponserID = searchParams.get('sponserID');
  const watchSponsor = watch("sponsor");

  const fetchAndShowSponsorDetails = async (id) => {
    try {
      const response = await sendApiRequest(`api/admin/partners/sponserGet/${id}`, {}, "GET");
      if (response.data.success) {
        const sponsor = response.data.data[0];
        Swal.fire({
          title: 'Sponsor Details',
          html: `
            <div style="text-align: center;">
              <p><strong>Name:</strong> ${sponsor.fullName}</p>
              <p><strong>Email:</strong> ${sponsor.email}</p>
              <p><strong>Phone:</strong> ${sponsor.phone}</p>
            </div>
          `,
          icon: 'info',
          confirmButtonText: 'Continue',
          confirmButtonColor: '#8F8431',
          background: '#F9F9F6'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Unable to fetch sponsor details',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#8F8431',
        background: '#F9F9F6'
      });
    }
  };

  useEffect(() => {
    if (sponserID) {
      setValue("sponsor", sponserID);
      fetchAndShowSponsorDetails(sponserID);
    }
  }, [sponserID, setValue]);

  useEffect(() => {
    if (watchSponsor && watchSponsor !== sponserID && watchSponsor.length >= 8) {
      fetchAndShowSponsorDetails(watchSponsor);
    }
  }, [watchSponsor, sponserID]);

  const onSubmit = async (data) => {
    try {
      const response = await sendApiRequest('api/auth/register', data, "POST");
      if (response.data.success) {
        Swal.fire({
          title: 'Registration Successful!',
          html: `
            <div style="text-align: center;">
              <p><strong>Full Name:</strong> ${response.data.data.user.fullName}</p>
              <p><strong>Email:</strong> ${response.data.data.user.email}</p>
              <p><strong>Username:</strong> ${response.data.data.user.username}</p>
              <p><strong>Sponsor Code:</strong> ${response.data.data.user.sponsorCode}</p>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'Go to Login',
          confirmButtonColor: '#8F8431',
          background: '#F9F9F6'
        }).then(() => {
          navigate('/login');
        });
      } else {
        Swal.fire({
          title: 'Registration Failed',
          text: response.data.message || 'Something went wrong',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#8F8431',
          background: '#F9F9F6'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Registration failed',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#8F8431',
        background: '#F9F9F6'
      });
    }
  };

  const states = ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"];
  const ranks = ["Associate"];
  const countries = ["India", "USA", "UK", "Canada", "Australia"];

  return (
    <div className="min-h-screen bg-[#EDE9E0] py-8 px-4">
      <div className="max-w-xl mx-auto">
        {/* Neomorphic Card */}
        <div className="bg-[#EDE9E0] rounded-3xl p-8 md:p-10 shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] border border-[#E5E0D0]">
          
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C3A2A]">Partner Registration</h2>
            <p className="text-[#5F6A5A] mt-2">Join the Dharaa Pariwar Network</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Full Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faUser} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="text"
                  placeholder="Enter full name"
                  {...register("fullName", { required: "Full Name is required" })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.fullName && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.fullName.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faEnvelope} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="email"
                  placeholder="Enter email"
                  {...register("email", { 
                    required: "Email is required", 
                    pattern: { value: /^\S+@\S+$/i, message: "Invalid email" } 
                  })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.email && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.email.message}</p>}
            </div>

            {/* Sponsor ID */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Sponsor ID</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faUserFriends} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="text"
                  placeholder="Enter Sponsor ID"
                  {...register("sponsor")}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Phone Number</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faPhone} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  {...register("phone", { 
                    required: "Phone number is required", 
                    pattern: { value: /^\d{10}$/, message: "10-digit phone number required" } 
                  })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.phone && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.phone.message}</p>}
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">City</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faCity} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="text"
                  placeholder="Enter city"
                  {...register("city", { required: "City is required" })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.city && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.city.message}</p>}
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">State</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-[#8F8431] text-xl" />
                </div>
                <select
                  {...register("state", { required: "State is required" })}
                  className="w-full pl-12 pr-10 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A] appearance-none"
                >
                  <option value="">Select State</option>
                  {states.map((state, i) => <option key={i} value={state}>{state}</option>)}
                </select>
              </div>
              {errors.state && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.state.message}</p>}
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Country</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faGlobe} className="text-[#8F8431] text-xl" />
                </div>
                <select
                  {...register("country", { required: "Country is required" })}
                  className="w-full pl-12 pr-10 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A] appearance-none"
                >
                  <option value="">Select Country</option>
                  {countries.map((country, i) => <option key={i} value={country}>{country}</option>)}
                </select>
              </div>
              {errors.country && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.country.message}</p>}
            </div>

            {/* Rank */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Rank</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-[#8F8431] text-xl" />
                </div>
                <select
                  {...register("rank", { required: "Rank is required" })}
                  className="w-full pl-12 pr-10 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A] appearance-none"
                >
                  <option value="">Select Rank</option>
                  {ranks.map((rank, i) => <option key={i} value={rank}>{rank}</option>)}
                </select>
              </div>
              {errors.rank && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.rank.message}</p>}
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faUser} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="text"
                  placeholder="Choose username"
                  {...register("username", { required: "Username is required" })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.username && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.username.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faLock} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="password"
                  placeholder="Create password"
                  {...register("password", { 
                    required: "Password is required", 
                    minLength: { value: 8, message: "Minimum 8 characters" } 
                  })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.password && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.password.message}</p>}
            </div>

            {/* Terms Checkbox */}
            <div className="flex items-start gap-3 pt-2">
              <input
                type="checkbox"
                {...register("terms", { required: "You must accept the terms" })}
                className="mt-1 w-5 h-5 accent-[#8F8431] cursor-pointer"
              />
              <label className="text-sm text-[#385532]">
                I agree to the <span className="text-[#8F8431] hover:underline cursor-pointer">Terms of Usage</span>
              </label>
            </div>
            {errors.terms && <p className="text-red-600 text-sm mt-1 ml-1">{errors.terms.message}</p>}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-6 py-4 rounded-2xl font-semibold text-lg bg-[#8F8431] text-white shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] active:shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E] transition-all flex items-center justify-center gap-3 disabled:opacity-70"
            >
              {isSubmitting ? <Loader /> : "Create Partner Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;